import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    PlayerDuelWrapper,
    MatchupWrapper,
    MatchupStateWrapper
} from './PlayerDuel.style';
import GDBREAKPOINTS from '@gameday/components/themes/breakpoints';
import useMedia from '@gameday/utils/hooks/useMedia';
import { liveViewsTypes } from '../themes/liveViewsTypes';
import OutsComponent from './components/Outs';
import BasePathComponent, { BASEPATH_MOBILE_WIDTH, BASEPATH_WIDTH } from './components/BasePathComponent';
import buildMatchupState from './utils/buildMatchupState';
import MatchupsList from './components/MatchupsList';
import MatchupDetails from './components/MatchupDetails/MatchupDetails';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
const EMPTY = Object.freeze({});

const { XS_MAX, LG_MAX, LG_MIN } = GDBREAKPOINTS;
const { FULLNARROW, SINGLENARROW } = liveViewsTypes;
const buildUpcomingBattersString = (inHole = {}, onDeck = {}, translate) => {
    const {
        person: {
            boxscoreName: inHoleBoxscoreName
        } = {},
        stats: {
            batting: {
                hits: inHoleHits,
                atBats: inHoleAtBats
            } = {}
        } = {}
    } = inHole;
    const {
        person: {
            boxscoreName: onDeckBoxscoreName
        } = {},
        stats: {
            batting: {
                hits: onDeckHits,
                atBats: onDeckAtBats
            } = {}
        } = {}
    } = onDeck;
    const onDeckBatterStats = typeof onDeck !== 'undefined' && `${translate('game_ondeck', 'On deck')}: ${onDeckBoxscoreName} ${onDeckHits} - ${onDeckAtBats}`;
    const inHoleBatterStats = typeof inHole !== 'undefined' && `${translate('game_inthehole', 'In the hole')}: ${inHoleBoxscoreName} ${inHoleHits} - ${inHoleAtBats}`;
    return [onDeckBatterStats, inHoleBatterStats].join(', ');
};

const PlayerDuel = ({
    probablePitchers,
    abstractGameCode,
    batter,
    inHole,
    onDeck,
    first,
    second,
    third,
    pitcher,
    balls,
    strikes,
    outs,
    showUpcomingBatters = true,
    gameStateOverride,
    deviceInteraction,
    usePreviousPlayers,
    stacked,
    playerDuelStats = EMPTY,
    isLoadingPlayerDuelStats
}) => {
    let viewType;
    const gameCodeOverride = gameStateOverride || abstractGameCode;
    const isMobile = useMedia(`(max-width: ${XS_MAX})`);
    const isFullNarrow = useMedia(`(min-width: ${LG_MIN}) and (max-width: ${LG_MAX})`);
    const matchupState = buildMatchupState({ gameCodeOverride, balls, strikes });
    const outWidth = isMobile ? BASEPATH_MOBILE_WIDTH : BASEPATH_WIDTH;
    const translate = useTranslate();

    if (isMobile) {
        viewType = SINGLENARROW;
    } else if (isFullNarrow) {
        viewType = FULLNARROW;
    };

    const isPreview = gameCodeOverride === 'P';
    const $gameState = isPreview ? 'preview' : '';
    const basepaths = { first: !!first?.person, second: !!second?.person, third: !!third?.person };

    const matchupStats = playerDuelStats[batter?.person?.id]?.opponents?.[pitcher?.person?.id];
    const {
        atBats = 0,
        hits = 0,
        avg = 0,
        homeRuns = 0,
        rbi = 0,
        strikeOuts = 0
    } = matchupStats || EMPTY;

    const HRLabel = translate('labelBrief_h_hr', 'HR');
    const RBILabel = translate('labelBrief_rbi', 'RBI');
    const strikeoutLabel = translate('labelBrief_h_k', 'K');
    const upcomingBatters = buildUpcomingBattersString(inHole, onDeck, translate);

    const formattedMatchUpStats = useMemo(() =>
        typeof matchupStats !== 'undefined' ? `vs. ${pitcher.person.boxscoreName}: ${hits} - ${atBats} (${avg}) ${homeRuns} ${HRLabel}, ${rbi} ${RBILabel}, ${strikeOuts} ${strikeoutLabel}` : ''
    , [isLoadingPlayerDuelStats]);
    const matchupStatMessages = useMemo(() => {
        const matchupMessages = [];
        if (formattedMatchUpStats.length > 0) { matchupMessages.push(formattedMatchUpStats); }
        if (upcomingBatters.length > 0) { matchupMessages.push(upcomingBatters); }
        return matchupMessages;
    }, [formattedMatchUpStats, upcomingBatters]);

    const showMatchupDetails = useMemo(() => {
        return matchupStatMessages.length > 0 && !isPreview && showUpcomingBatters;
    }, [isPreview, showUpcomingBatters, matchupStatMessages]);

    const matchupsArray = MatchupsList({
        probablePitchers,
        abstractGameCode,
        batter,
        pitcher,
        gameStateOverride,
        stacked: isPreview ? true : (!!isMobile || stacked || isFullNarrow),
        deviceInteraction,
        usePreviousPlayers,
        viewType,
        isMobile,
        isFullNarrow,
        headshotWidth: isPreview ? '96px' : '64px'
    });

    return (
        <>
            <PlayerDuelWrapper $gameState={$gameState} $type={viewType}>
                { matchupsArray.length && (
                    <MatchupWrapper $gameState={$gameState} $showUpcomingBatters={showUpcomingBatters} $type={viewType}>
                        { matchupsArray[0] }
                        <MatchupStateWrapper $gameState={$gameState} $type={viewType}>
                            {!isPreview && <BasePathComponent {...basepaths} width={outWidth} />}
                            <span>{matchupState}</span>
                            {!isPreview && <OutsComponent outCount={outs} outWidth={outWidth} />}
                        </MatchupStateWrapper>
                        { matchupsArray[1] }
                    </MatchupWrapper>
                )}
            </PlayerDuelWrapper>
            { showMatchupDetails && <MatchupDetails isLoading={isLoadingPlayerDuelStats} matchupMessages={matchupStatMessages} isMobile={isMobile}/> }
        </>
    );
};

PlayerDuel.propTypes = {
    first: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    second: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    third: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    isMobile: PropTypes.bool,
    activeColor: PropTypes.string,
    probablePitchers: PropTypes.object,
    abstractGameCode: PropTypes.string,
    batter: PropTypes.object,
    inHole: PropTypes.object,
    onDeck: PropTypes.object,
    pitcher: PropTypes.object,
    balls: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    strikes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    outs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showUpcomingBatters: PropTypes.bool,
    gameStateOverride: PropTypes.string,
    stacked: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    usePreviousPlayers: PropTypes.bool,
    playerDuelStats: PropTypes.object,
    isLoadingPlayerDuelStats: PropTypes.bool
};

export default PlayerDuel;
